import { svg, type TemplateResult } from "lit";

import type { SbAuthProvider } from "@/types";

import type {
  BaseButtonContent,
  BaseButtonIconAlign,
  BaseButtonSize,
  BaseButtonVariant,
  PasskeyButtonContent,
  PasskeyButtonIconAlign,
  PasskeyButtonSize,
  PasskeyButtonVariant,
  SbPasskeyButtonProps as PasskeyProps,
  ProviderButtonContent,
  ProviderButtonIconAlign,
  ProviderButtonSize,
  ProviderButtonVariant,
  SbProviderButtonProps as ProviderProps,
  SubmitButtonContent,
  SubmitButtonIconAlign,
  SubmitButtonSize,
  SubmitButtonVariant,
  SbSubmitButtonProps as SubmitProps,
} from "./button.types";

export const PASSKEY_BUTTON_DEFAULT_PROPS: Omit<PasskeyProps, "seed"> = {
  variant: "secondary",
  size: "default",
  content: "label",
  iconAlign: "start",
  label: "Use Passkey",
};

export const PROVIDER_BUTTON_DEFAULT_PROPS: Omit<
  ProviderProps,
  "seed" | "provider"
> = {
  variant: "outline",
  size: "default",
  content: "icon-label",
  iconAlign: "start",
  prompt: "Use",
};

export const SUBMIT_BUTTON_DEFAULT_PROPS: Omit<SubmitProps, "seed"> = {
  variant: "default",
  size: "default",
  content: "label",
  iconAlign: "end",
  label: "Submit",
};

/* ------------------------------------------------------------ */

const VARIANTS_REC: Record<BaseButtonVariant, BaseButtonVariant> = {
  default: "default",
  secondary: "secondary",
  outline: "outline",
  ghost: "ghost",
};

const SIZES_REC: Record<BaseButtonSize, BaseButtonSize> = {
  xs: "xs",
  sm: "sm",
  default: "default",
  lg: "lg",
};

const CONTENTS_REC: Record<BaseButtonContent, BaseButtonContent> = {
  icon: "icon",
  "icon-label": "icon-label",
  label: "label",
};

const ICON_ALIGNS_REC: Record<BaseButtonIconAlign, BaseButtonIconAlign> = {
  start: "start",
  end: "end",
};

/* ---------------------------------------- */

export const PASSKEY_BUTTON_VARIANTS: PasskeyButtonVariant[] =
  Object.values(VARIANTS_REC);
export const PASSKEY_BUTTON_SIZES: PasskeyButtonSize[] =
  Object.values(SIZES_REC);
export const PASSKEY_BUTTON_CONTENTS: PasskeyButtonContent[] =
  Object.values(CONTENTS_REC);
export const PASSKEY_BUTTON_ICON_ALIGNS: PasskeyButtonIconAlign[] =
  Object.values(ICON_ALIGNS_REC);

/* ---------------------------------------- */

export const PROVIDER_BUTTON_VARIANTS: ProviderButtonVariant[] =
  Object.values(VARIANTS_REC);
export const PROVIDER_BUTTON_SIZES: ProviderButtonSize[] =
  Object.values(SIZES_REC);
export const PROVIDER_BUTTON_CONTENTS: ProviderButtonContent[] =
  Object.values(CONTENTS_REC);
export const PROVIDER_BUTTON_ICON_ALIGNS: ProviderButtonIconAlign[] =
  Object.values(ICON_ALIGNS_REC);

/* ---------------------------------------- */

export const SUBMIT_BUTTON_VARIANTS: SubmitButtonVariant[] =
  Object.values(VARIANTS_REC);
export const SUBMIT_BUTTON_SIZES: SubmitButtonSize[] = Object.values(SIZES_REC);
export const SUBMIT_BUTTON_CONTENTS: SubmitButtonContent[] =
  Object.values(CONTENTS_REC);
export const SUBMIT_BUTTON_ICON_ALIGNS: SubmitButtonIconAlign[] =
  Object.values(ICON_ALIGNS_REC);

/* ---------------------------------------- */

const AUTH_PROVIDERS_REC: Record<SbAuthProvider, SbAuthProvider> = {
  apple: "apple",
  discord: "discord",
  facebook: "facebook",
  github: "github",
  google: "google",
  linkedin: "linkedin",
  meta: "meta",
  microsoft: "microsoft",
  x: "x",
};

export const AUTH_PROVIDERS: SbAuthProvider[] =
  Object.values(AUTH_PROVIDERS_REC);

/* ------------------------------------------------------------ */

export const PROVIDER_NAMES: Record<SbAuthProvider, string> = {
  apple: "Apple",
  discord: "Discord",
  facebook: "Facebook",
  github: "GitHub",
  google: "Google",
  linkedin: "LinkedIn",
  meta: "Meta",
  microsoft: "Microsoft",
  x: "X",
};

export const PROVIDER_ICON_FRAGMENTS: Record<
  SbAuthProvider,
  TemplateResult<2>
> = {
  apple: svg`<path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />`,
  discord: svg`<path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z" />`,
  facebook: svg`<path d="M9.101 23.691v-7.98H6.627v-3.667h2.474v-1.58c0-4.085 1.848-5.978 5.858-5.978.401 0 .955.042 1.468.103a8.68 8.68 0 0 1 1.141.195v3.325a8.623 8.623 0 0 0-.653-.036 26.805 26.805 0 0 0-.733-.009c-.707 0-1.259.096-1.675.309a1.686 1.686 0 0 0-.679.622c-.258.42-.374.995-.374 1.752v1.297h3.919l-.386 2.103-.287 1.564h-3.246v8.245C19.396 23.238 24 18.179 24 12.044c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.628 3.874 10.35 9.101 11.647Z" />`,
  github: svg`<path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />`,
  google: svg`<path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />`,
  linkedin: svg`<path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />`,
  meta: svg`<path d="M6.915 4.03c-1.968 0-3.683 1.28-4.871 3.113C.704 9.208 0 11.883 0 14.449c0 .706.07 1.369.21 1.973a6.624 6.624 0 0 0 .265.86 5.297 5.297 0 0 0 .371.761c.696 1.159 1.818 1.927 3.593 1.927 1.497 0 2.633-.671 3.965-2.444.76-1.012 1.144-1.626 2.663-4.32l.756-1.339.186-.325c.061.1.121.196.183.3l2.152 3.595c.724 1.21 1.665 2.556 2.47 3.314 1.046.987 1.992 1.22 3.06 1.22 1.075 0 1.876-.355 2.455-.843a3.743 3.743 0 0 0 .81-.973c.542-.939.861-2.127.861-3.745 0-2.72-.681-5.357-2.084-7.45-1.282-1.912-2.957-2.93-4.716-2.93-1.047 0-2.088.467-3.053 1.308-.652.57-1.257 1.29-1.82 2.05-.69-.875-1.335-1.547-1.958-2.056-1.182-.966-2.315-1.303-3.454-1.303zm10.16 2.053c1.147 0 2.188.758 2.992 1.999 1.132 1.748 1.647 4.195 1.647 6.4 0 1.548-.368 2.9-1.839 2.9-.58 0-1.027-.23-1.664-1.004-.496-.601-1.343-1.878-2.832-4.358l-.617-1.028a44.908 44.908 0 0 0-1.255-1.98c.07-.109.141-.224.211-.327 1.12-1.667 2.118-2.602 3.358-2.602zm-10.201.553c1.265 0 2.058.791 2.675 1.446.307.327.737.871 1.234 1.579l-1.02 1.566c-.757 1.163-1.882 3.017-2.837 4.338-1.191 1.649-1.81 1.817-2.486 1.817-.524 0-1.038-.237-1.383-.794-.263-.426-.464-1.13-.464-2.046 0-2.221.63-4.535 1.66-6.088.454-.687.964-1.226 1.533-1.533a2.264 2.264 0 0 1 1.088-.285z" />`,
  microsoft: svg`<path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z" />`,
  x: svg`<path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.763l7.726-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />`,
};
